import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { parseError } from '@/lib/errors'
import { formatDate } from '@/lib/formatters'
import { type PolicyChangeRequest, policyChangeWrites, usePolicyChangeMutation } from '@/models/policyChange'
import { ClaimStepShell } from '../Claims/ClaimStepShell'
import { changeLines, KIND_LABELS, toIsoDate } from './policyChangeCopy'

/**
 * The read-back, the certification and the signature that sends the request to the broker. Staff sending a
 * request they built for the customer (forCustomer) skip the certification: it goes unsigned, straight to review.
 */
export function StepReview({
  orgId,
  request,
  onBack,
  forCustomer = false,
}: {
  orgId: string
  request: PolicyChangeRequest
  onBack: () => void
  forCustomer?: boolean
}) {
  const navigate = useNavigate()
  const submit = usePolicyChangeMutation(orgId, policyChangeWrites.submit)
  const submitForCustomer = usePolicyChangeMutation(orgId, policyChangeWrites.submitForCustomer)
  const [accurate, setAccurate] = useState(false)
  const [quoteFirst, setQuoteFirst] = useState(false)
  const [signature, setSignature] = useState('')
  const lines = request.changes.filter((line) => !line.dropped)

  async function send() {
    try {
      if (forCustomer) {
        await submitForCustomer.mutateAsync(request.id)
        toast.success('Sent for the customer. It is in review.')
        navigate(`/insure/internal/endorsements/${request.id}?view=open`)
        return
      }
      await submit.mutateAsync({ requestId: request.id, signature })
      toast.success('Request sent. Your insurance broker is reviewing it.')
      navigate(`/insure/policy-changes/${request.id}`)
    } catch (error) {
      toast.error(parseError(error))
    }
  }
  const sending = submit.isPending || submitForCustomer.isPending

  return (
    <ClaimStepShell
      step={4}
      stepCount={4}
      title={forCustomer ? 'Check the request' : 'Check your request'}
      subtitle={
        forCustomer
          ? 'It goes to review unsigned, as a request Cai took for the customer.'
          : 'Your insurance broker reviews it and sends it to your insurance providers.'
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {lines.length} change{lines.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col divide-y divide-neutral-100">
          {lines.map((line) => (
            <div key={line.id} className="flex items-baseline justify-between gap-4 py-2 text-sm">
              <div>
                <div className="font-medium">{KIND_LABELS[line.kind]}</div>
                {changeLines(line).map((text) => (
                  <div key={text} className="text-neutral-600">
                    {text}
                  </div>
                ))}
              </div>
              <span className="shrink-0 text-neutral-600">From {formatDate(line.effectiveDate)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Banner variant="info" small>
        {forCustomer
          ? 'The insurance companies quote any price change. Nothing changes on the policies until the customer accepts it.'
          : 'Your insurance providers quote any price change. Nothing changes on your policies until you accept it.'}
      </Banner>
      {!forCustomer && (
        <Card>
          <CardHeader>
            <CardTitle>Certification</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                className="mt-0.5"
                checked={accurate}
                onCheckedChange={(checked) => setAccurate(checked === true)}
              />
              <span className="text-sm text-neutral-700">
                The information in this request is accurate and complete to the best of my knowledge.
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                className="mt-0.5"
                checked={quoteFirst}
                onCheckedChange={(checked) => setQuoteFirst(checked === true)}
              />
              <span className="text-sm text-neutral-700">
                I understand my price may change, and I'll be asked to accept the quote before anything
                changes.
              </span>
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="policy-change-signature">Digital signature (full legal name)</FieldLabel>
                <Input
                  id="policy-change-signature"
                  placeholder="Type your full legal name"
                  value={signature}
                  onChange={(event) => setSignature(event.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="policy-change-date">Date</FieldLabel>
                <Input id="policy-change-date" value={formatDate(toIsoDate(new Date()))} readOnly disabled />
              </Field>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="flex justify-between">
        <Button type="button" variant="outline" disabled={sending} onClick={onBack}>
          ← Back
        </Button>
        <Button
          type="button"
          disabled={!forCustomer && (!accurate || !quoteFirst || !signature.trim())}
          onClick={send}
          loading={sending ? 'Sending…' : undefined}
        >
          {forCustomer ? 'Send for the customer' : 'Send request'}
        </Button>
      </div>
    </ClaimStepShell>
  )
}
